import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  AutoAwesome,
  Person,
  Image as ImageIcon,
  PhotoLibrary,
  Refresh,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDevice } from '../contexts/DeviceContext';
import { favouriteCharacterService, FavouriteCharacterImage, ImageSource } from '../services/favouriteCharacterService';

const MAX_IMAGES = 3;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_FORMATS = ['image/jpeg', 'image/png', 'image/gif'];

const FavouriteCharacterSection: React.FC = () => {
  const { t } = useTranslation();
  const { selectedDevice, showSnackbar } = useDevice();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [characterName, setCharacterName] = useState('');
  const [images, setImages] = useState<FavouriteCharacterImage[]>([]);
  const [imageSource, setImageSource] = useState<ImageSource>('custom');

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);
  const [switchConfirmOpen, setSwitchConfirmOpen] = useState(false);
  const [pendingSwitch, setPendingSwitch] = useState<ImageSource | null>(null);
  const [generatingSlots, setGeneratingSlots] = useState<Set<number>>(new Set());

  const fileInputRef = useRef<HTMLInputElement>(null);
  const nameDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Load favourite character data
  const loadData = useCallback(async () => {
    if (!selectedDevice?.id) return;

    try {
      setLoading(true);
      const data = await favouriteCharacterService.getFavouriteCharacter(selectedDevice.id);
      setCharacterName(data.name || '');
      setImages(data.images || []);
      setImageSource(data.imageSource || 'custom');
    } catch (error) {
      console.error('Failed to load favourite character:', error);
      showSnackbar(t('favouriteCharacter.failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedDevice?.id, showSnackbar, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-save name with debounce
  const handleNameChange = (value: string) => {
    setCharacterName(value);

    if (nameDebounceRef.current) {
      clearTimeout(nameDebounceRef.current);
    }

    nameDebounceRef.current = setTimeout(async () => {
      if (!selectedDevice?.id) return;

      try {
        setSaving(true);
        await favouriteCharacterService.updateFavouriteCharacter(selectedDevice.id, value || null);
        showSnackbar(t('favouriteCharacter.nameSaved'), 'success');
      } catch (error) {
        console.error('Failed to save name:', error);
        showSnackbar(t('favouriteCharacter.failedToSaveName'), 'error');
      } finally {
        setSaving(false);
      }
    }, 1000);
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (nameDebounceRef.current) {
        clearTimeout(nameDebounceRef.current);
      }
    };
  }, []);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedDevice?.id) return;

    // Validate file
    if (!ALLOWED_FORMATS.includes(file.type)) {
      showSnackbar(t('favouriteCharacter.invalidFormat'), 'error');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showSnackbar(t('favouriteCharacter.fileTooLarge'), 'error');
      return;
    }

    try {
      setUploading(true);
      const data = await favouriteCharacterService.uploadImage(selectedDevice.id, file);
      setImages(data.images);
      setCharacterName(data.name || '');
      showSnackbar(t('favouriteCharacter.imageUploaded'), 'success');
    } catch (error: any) {
      console.error('Failed to upload image:', error);
      showSnackbar(error.message || t('favouriteCharacter.failedToUpload'), 'error');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async () => {
    if (!imageToDelete || !selectedDevice?.id) return;

    try {
      setSaving(true);
      const data = await favouriteCharacterService.deleteImage(selectedDevice.id, imageToDelete);
      setImages(data.images);
      showSnackbar(t('favouriteCharacter.imageDeleted'), 'success');
    } catch (error) {
      console.error('Failed to delete image:', error);
      showSnackbar(t('favouriteCharacter.failedToDelete'), 'error');
    } finally {
      setSaving(false);
      setDeleteConfirmOpen(false);
      setImageToDelete(null);
    }
  };

  const openDeleteConfirm = (imageId: string) => {
    setImageToDelete(imageId);
    setDeleteConfirmOpen(true);
  };

  const handleImageSourceChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: ImageSource | null
  ) => {
    if (!newValue || newValue === imageSource) return;

    // Switching to AI requires character name
    if (newValue === 'ai' && !characterName.trim()) {
      showSnackbar(t('favouriteCharacter.enterNameFirst'), 'error');
      return;
    }

    // If images exist, confirm before clearing
    if (images.length > 0) {
      setPendingSwitch(newValue);
      setSwitchConfirmOpen(true);
      return;
    }

    // No images, switch directly
    executeImageSourceSwitch(newValue);
  };

  const executeImageSourceSwitch = async (newValue: ImageSource) => {
    if (!selectedDevice?.id) return;

    try {
      // Delete existing images first
      for (const img of images) {
        try {
          await favouriteCharacterService.deleteImage(selectedDevice.id, img.id);
        } catch (error) {
          console.error('Failed to delete image:', error);
        }
      }

      // Update image source in Firebase
      setImageSource(newValue);
      setImages([]);
      await favouriteCharacterService.updateFavouriteCharacter(
        selectedDevice.id,
        characterName || null,
        newValue
      );

      // If switching to AI, trigger generation for all 3 slots
      if (newValue === 'ai') {
        // Start generating all 3 slots in parallel
        setGeneratingSlots(new Set([0, 1, 2]));

        // Generate each image independently
        for (let slotIndex = 0; slotIndex < MAX_IMAGES; slotIndex++) {
          // Don't await - let them run in parallel
          generateSingleSlot(slotIndex);
        }
      }
    } catch (error) {
      console.error('Failed to switch image source:', error);
      showSnackbar(t('favouriteCharacter.failedToSave'), 'error');
    }
  };

  const generateSingleSlot = async (slotIndex: number) => {
    if (!selectedDevice?.id || !characterName.trim()) return;

    try {
      const response = await favouriteCharacterService.generateSingleAIImage(
        selectedDevice.id,
        characterName.trim(),
        slotIndex
      );
      // Add the new image to the list
      setImages(prev => [...prev, response.image]);
    } catch (error: any) {
      console.error(`Failed to generate image for slot ${slotIndex}:`, error);
      // Don't show snackbar for each failure during initial generation
    } finally {
      setGeneratingSlots(prev => {
        const next = new Set(prev);
        next.delete(slotIndex);
        return next;
      });
    }
  };

  const handleConfirmSwitch = () => {
    if (pendingSwitch) {
      executeImageSourceSwitch(pendingSwitch);
    }
    setSwitchConfirmOpen(false);
    setPendingSwitch(null);
  };

  const handleCancelSwitch = () => {
    setSwitchConfirmOpen(false);
    setPendingSwitch(null);
  };

  const handleRegenerateSlot = async (slotIndex: number) => {
    if (!selectedDevice?.id || !characterName.trim()) return;

    // Mark this slot as generating
    setGeneratingSlots(prev => new Set(prev).add(slotIndex));

    try {
      const response = await favouriteCharacterService.generateSingleAIImage(
        selectedDevice.id,
        characterName.trim(),
        slotIndex
      );
      // Add the new image to the list
      setImages(prev => [...prev, response.image]);
      showSnackbar(response.message, 'success');
    } catch (error: any) {
      console.error('Failed to generate image:', error);
      showSnackbar(error.message || t('favouriteCharacter.failedToGenerate'), 'error');
    } finally {
      setGeneratingSlots(prev => {
        const next = new Set(prev);
        next.delete(slotIndex);
        return next;
      });
    }
  };

  const handleAddImageClick = () => {
    fileInputRef.current?.click();
  };

  // Get image for a specific slot
  const getImageForSlot = (slotIndex: number) => {
    return images.find(img => img.slotIndex === slotIndex);
  };

  // Calculate which slots are empty (don't have an image and aren't generating)
  const getEmptyAISlots = (): number[] => {
    if (imageSource !== 'ai') return [];
    const emptySlots: number[] = [];
    for (let i = 0; i < MAX_IMAGES; i++) {
      if (!getImageForSlot(i) && !generatingSlots.has(i)) {
        emptySlots.push(i);
      }
    }
    return emptySlots;
  };

  const emptyAISlots = getEmptyAISlots();
  const canAddMore = images.length < MAX_IMAGES;
  const isReadOnly = selectedDevice?.role === 'viewer';

  if (!selectedDevice) return null;

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('favouriteCharacter.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('favouriteCharacter.description')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Character Name Input */}
            <TextField
              fullWidth
              label={t('favouriteCharacter.characterName')}
              placeholder={t('favouriteCharacter.namePlaceholder')}
              value={characterName}
              onChange={(e) => handleNameChange(e.target.value)}
              disabled={isReadOnly || saving}
              InputProps={{
                startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: saving ? <CircularProgress size={20} /> : null,
              }}
              sx={{ mb: 2 }}
            />

            {/* Image Source Toggle */}
            {!isReadOnly && (
              <Box sx={{ mb: 3 }}>
                <ToggleButtonGroup
                  value={imageSource}
                  exclusive
                  onChange={handleImageSourceChange}
                  fullWidth
                  disabled={generatingSlots.size > 0}
                  sx={{
                    '& .MuiToggleButton-root': {
                      py: 1.5,
                      textTransform: 'none',
                    },
                  }}
                >
                  <ToggleButton value="ai">
                    <AutoAwesome sx={{ mr: 1 }} />
                    {t('favouriteCharacter.imageSource.ai')}
                  </ToggleButton>
                  <ToggleButton value="custom">
                    <PhotoLibrary sx={{ mr: 1 }} />
                    {t('favouriteCharacter.imageSource.custom')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {/* Images Section */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('favouriteCharacter.images')} ({images.length}/{MAX_IMAGES})
              </Typography>

              {images.length === 0 && emptyAISlots.length === 0 && generatingSlots.size === 0 ? (
                // No images and not in AI mode
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                  }}
                >
                  <ImageIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">
                    {t('favouriteCharacter.noImages')}
                  </Typography>
                </Box>
              ) : imageSource === 'ai' ? (
                // AI mode: Show all 3 slots in order
                <ImageList cols={3} gap={8} sx={{ mt: 1 }}>
                  {[0, 1, 2].map((slotIndex) => {
                    const img = getImageForSlot(slotIndex);
                    const isGenerating = generatingSlots.has(slotIndex);

                    if (img) {
                      // Slot has an image
                      return (
                        <ImageListItem key={`slot-${slotIndex}`} sx={{ borderRadius: 1, overflow: 'hidden' }}>
                          <img
                            src={img.thumbnailUrl}
                            alt="Character"
                            loading="lazy"
                            style={{ height: 120, objectFit: 'cover' }}
                          />
                          {!isReadOnly && (
                            <ImageListItemBar
                              sx={{ background: 'transparent' }}
                              position="top"
                              actionPosition="right"
                              actionIcon={
                                <Tooltip title={t('common.delete')}>
                                  <IconButton
                                    size="small"
                                    onClick={() => openDeleteConfirm(img.id)}
                                    sx={{
                                      bgcolor: 'error.main',
                                      color: 'white',
                                      m: 0.5,
                                      '&:hover': { bgcolor: 'error.dark' },
                                    }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              }
                            />
                          )}
                        </ImageListItem>
                      );
                    } else if (isGenerating) {
                      // Slot is generating
                      return (
                        <ImageListItem
                          key={`slot-${slotIndex}`}
                          sx={{
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: '2px dashed',
                            borderColor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 120,
                            bgcolor: 'action.hover',
                          }}
                        >
                          <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress size={32} />
                          </Box>
                        </ImageListItem>
                      );
                    } else {
                      // Slot is empty - show regenerate button
                      return (
                        <ImageListItem
                          key={`slot-${slotIndex}`}
                          sx={{
                            borderRadius: 1,
                            overflow: 'hidden',
                            border: '2px dashed',
                            borderColor: 'divider',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: 120,
                            bgcolor: 'action.hover',
                          }}
                        >
                          {!isReadOnly && (
                            <Tooltip title={t('favouriteCharacter.regenerate')}>
                              <IconButton
                                onClick={() => handleRegenerateSlot(slotIndex)}
                                sx={{
                                  bgcolor: 'primary.main',
                                  color: 'white',
                                  '&:hover': { bgcolor: 'primary.dark' },
                                }}
                              >
                                <Refresh />
                              </IconButton>
                            </Tooltip>
                          )}
                        </ImageListItem>
                      );
                    }
                  })}
                </ImageList>
              ) : (
                // Custom mode: Show images only
                <ImageList cols={3} gap={8} sx={{ mt: 1 }}>
                  {images.map((img) => (
                    <ImageListItem key={img.id} sx={{ borderRadius: 1, overflow: 'hidden' }}>
                      <img
                        src={img.thumbnailUrl}
                        alt="Character"
                        loading="lazy"
                        style={{ height: 120, objectFit: 'cover' }}
                      />
                      {!isReadOnly && (
                        <ImageListItemBar
                          sx={{ background: 'transparent' }}
                          position="top"
                          actionPosition="right"
                          actionIcon={
                            <Tooltip title={t('common.delete')}>
                              <IconButton
                                size="small"
                                onClick={() => openDeleteConfirm(img.id)}
                                sx={{
                                  bgcolor: 'error.main',
                                  color: 'white',
                                  m: 0.5,
                                  '&:hover': { bgcolor: 'error.dark' },
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          }
                        />
                      )}
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
            </Box>

            {/* Add Image Button - only show for custom image source */}
            {!isReadOnly && canAddMore && imageSource === 'custom' && (
              <Button
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={20} /> : <CloudUpload />}
                onClick={handleAddImageClick}
                disabled={uploading}
                fullWidth
              >
                {uploading ? t('favouriteCharacter.uploading') : t('favouriteCharacter.addImage')}
              </Button>
            )}

            {!canAddMore && !isReadOnly && imageSource === 'custom' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('favouriteCharacter.maxImagesReached')}
              </Alert>
            )}
          </>
        )}
      </CardContent>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        hidden
        accept="image/jpeg,image/png,image/gif"
        onChange={handleFileSelect}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>{t('favouriteCharacter.deleteImage')}</DialogTitle>
        <DialogContent>
          <Typography>{t('favouriteCharacter.deleteConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteImage}
            color="error"
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Switch Image Source Confirmation Dialog */}
      <Dialog open={switchConfirmOpen} onClose={handleCancelSwitch}>
        <DialogTitle>
          {pendingSwitch === 'ai'
            ? t('favouriteCharacter.imageSource.confirmSwitchToAi.title')
            : t('favouriteCharacter.imageSource.confirmSwitchToCustom.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingSwitch === 'ai'
              ? t('favouriteCharacter.imageSource.confirmSwitchToAi.message')
              : t('favouriteCharacter.imageSource.confirmSwitchToCustom.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSwitch}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmSwitch}
            color="primary"
            variant="contained"
          >
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default FavouriteCharacterSection;
